import compact from "lodash/compact";
import find from "lodash/find";
import sortBy from "lodash/sortBy";
import ComparePage from "PFApp/activities/compare/compare_page";
import { ReassignActionContextProvider } from "PFApp/booking/parts/overview/booking_module_calendar/context/reassign_action_context_provider";
import { PageWrapper } from "PFComponents/page_wrapper";
import { useActivity } from "PFCore/hooks/queries/activity";
import { useInfiniteActivityWatchers } from "PFCore/hooks/queries/activity/use_activity_watchers";
import { useActivityRankedShortlists } from "PFCore/hooks/queries/shortlists/use_activity_ranked_shortlists";
import { useActivityShortlists } from "PFCore/hooks/queries/shortlists/use_activity_shortlists";
import { useActivitySuspendedShortlists } from "PFCore/hooks/queries/shortlists/use_activity_suspended_shortlists";
import { fetchInvites } from "PFCore/services/invites/fetch_invites";
import { fetchProfile } from "PFCore/services/profile";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";

const fetchData = async (id, profileIds, shortlists) => {
  const shortlistsWithInvite = await fetchInvites({ targetType: "Task", targetId: id }).then(
    ({ entries: invites }) =>
      sortBy(
        (shortlists || []).map((shortlist) => ({
          ...shortlist,
          invite: find(invites, { profileId: shortlist.profile.id })
        })),
        ["id"]
      )
  );

  const profilesWithFullData = await Promise.all(
    (profileIds || shortlists.map(({ profile }) => profile.id)).map((profileId) => fetchProfile(profileId))
  );

  return {
    shortlistsWithInvite,
    profilesWithFullData
  };
};

const CompareController = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, tab, ids, type } = useParams();

  const initProfileIds = ids ? compact(ids.split(",")).map((id) => parseInt(id, 10)) : null;

  const [profileIds, setProfilesIds] = useState(initProfileIds);
  const [shortlists, setShortlists] = useState(null);
  const [rankLoaded, setRankLoaded] = useState(false);
  const [profilesWithFullData, setProfilesWithFullData] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const prevPath = useRef(location.state?.prevPath);

  const isInterestedType = type === "interested";
  const { watchers } = useInfiniteActivityWatchers({ id }, 1000, {}, {}, { enabled: isInterestedType });
  const watchersWithShortlists = watchers.map(
    (watcher) => (shortlists || []).find(({ profile }) => profile.id === watcher.profile.id) || watcher
  );

  const exitToShortlist = () => navigate(`/activities/${id}/shortlist`);
  const exitToDetails = () => navigate(`/activities/${id}/details`);
  const exitToInterested = () => navigate(`/activities/${id}/interested`);

  const updateData = (shortlists) => {
    const activeProfileIds = profileIds
      ? profileIds.filter((profileId) => shortlists.find(({ profile }) => profile.id === profileId))
      : profileIds;

    return fetchData(id, activeProfileIds, shortlists)
      .then(({ shortlistsWithInvite, profilesWithFullData }) => {
        if (shortlistsWithInvite.length === 0 && !isInterestedType) {
          exitToShortlist();
        }
        setShortlists(shortlistsWithInvite);
        setProfilesWithFullData(profilesWithFullData);
        setIsLoaded(true);
        setRankLoaded(false);
      })
      .catch(exitToDetails);
  };

  const { data: activity, isSuccess: isActivitySuccess } = useActivity(id);
  const { data: shortlistsResponse, dataUpdatedAt: shortlistsDataUpdatedAt } = useActivityShortlists(
    id,
    {},
    {
      enabled: isActivitySuccess
    }
  );

  useEffect(() => {
    if (!shortlistsResponse) {
      return;
    }
    updateData(shortlistsResponse.entries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortlistsDataUpdatedAt]);

  const { data: rankedShortlistsResponse, dataUpdatedAt: rankedShortlistsDataUpdatedAt } =
    useActivityRankedShortlists({
      activityId: id,
      options: {
        enabled: !!shortlists && shortlists.length > 0 && !rankLoaded
      }
    });

  useEffect(() => {
    if (!rankedShortlistsResponse) {
      return;
    }
    const rankedShortlists = shortlists?.map((shortlist) => {
      const rank = rankedShortlistsResponse.entries.find(
        ({ profile }) => profile.id === shortlist.profile.id
      );

      return {
        ...(rank || {}),
        ...shortlist,
        profile: {
          ...(rank?.profile || {}),
          ...shortlist.profile
        }
      };
    });
    setRankLoaded(true);
    setShortlists(rankedShortlists);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankedShortlistsDataUpdatedAt]);

  const { data: suspendedShortlistsData } = useActivitySuspendedShortlists(id);

  const getPath = (newTab, ids) => {
    newTab = newTab === "availability" ? newTab : "details";

    let rest = "";
    if (ids === "__RETAIN__") {
      rest = `/${profileIds ? profileIds.join(",") : ""}`;
    } else if (ids) {
      rest = `/${ids.join(",")}`;
    }
    if (type) {
      rest = rest.concat(`/${type}`);
    }

    return `/activities/${id}/compare/${newTab}${rest}`;
  };

  const changeTab = (newTab) =>
    navigate({ pathname: getPath(newTab, profileIds, type) }, { state: { prevPath: prevPath.current } });

  useEffect(() => changeTab(tab), []); // if invalid tab goes to details

  const removeProfileId = (id) => {
    const ids = profileIds ? profileIds : shortlists.map(({ profile: { id } }) => id);
    setProfilesIds(ids.filter((pid) => pid !== id));
  };

  useEffect(() => {
    // null is valid (means all of the ids), any array is a subset
    if (profileIds && profileIds.length === 0) {
      isInterestedType ? exitToInterested() : exitToShortlist();
    } else {
      navigate({ pathname: getPath(tab, profileIds) }, { state: { prevPath: prevPath.current } });
    }
  }, [profileIds]);

  const filteredProfiles = useMemo(() => {
    const suspendedShortlists = suspendedShortlistsData?.entries || [];
    const shortlistsWithSuspended = [...(shortlists ?? []), ...(suspendedShortlists ?? [])];
    const profiles = (isInterestedType ? watchersWithShortlists : shortlistsWithSuspended) || [];

    return profiles.filter(({ profile: { id } }) => !profileIds || profileIds.includes(id));
  }, [shortlists, suspendedShortlistsData, profileIds, isInterestedType, watchersWithShortlists]);

  const comparableProfiles = filteredProfiles.map((item) => {
    const profileFullData = profilesWithFullData.find(({ id }) => id === item.profile.id);
    return {
      ...item,
      isAvailable: !!profileFullData,
      availabilityMatch: item.scores?.availabilityScore,
      profile: { ...item.profile, ...(profileFullData ?? {}) }
    };
  });

  return (
    <ReassignActionContextProvider>
      <PageWrapper isLoading={!isLoaded}>
        <ComparePage
          tab={tab}
          changeTab={changeTab}
          comparableProfiles={comparableProfiles}
          removeProfileId={removeProfileId}
          activity={activity}
        />
      </PageWrapper>
    </ReassignActionContextProvider>
  );
};

export default CompareController;
